"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import cn from "classnames"
import { gsap } from "gsap"

import buttonS from "~/components/primitives/Button/PrimaryButton.module.scss"
import { useModal } from "~/contexts/ModalContext"
import type { CtaLink } from "~/types/cms"

import s from "./MainNav.module.scss"

type MainNavProps = {
  links?: CtaLink[]
  isOpen: boolean
  isFullSize: boolean
  setIsOpen: (isOpen: boolean) => void
}

const STAGGER_DELAY = 0.1

export const MainNav = ({
  links,
  isOpen,
  isFullSize,
  setIsOpen,
}: MainNavProps) => {
  const navRef = useRef<HTMLDivElement>(null)
  const routeParam = usePathname()
  const { open: openContactModal } = useModal("contact")

  const isNavLinkActive = (linkUrl: string) => {
    if (linkUrl === "/") return routeParam === "/"

    return routeParam === linkUrl || routeParam.startsWith(`${linkUrl}/`)
  }
  const linkRefs = useRef<HTMLLIElement[]>([])
  const [activeLink, setActiveLink] = useState<HTMLLIElement | null>(null)
  const pillRef = useRef<HTMLDivElement>(null)

  const getPillDims = (activeLink: HTMLLIElement) => {
    let dims: { [key: string]: number | string } = {
      width: 0,
      height: 0,
      left: 0,
      top: 0,
    }

    if (!pillRef.current || !activeLink) return dims

    const pill = pillRef.current

    const {
      left: targetLeft,
      top: targetTop,
      width: targetWidth,
      height: targetHeight,
    } = activeLink.getBoundingClientRect()

    const inlinePadding = getComputedStyle(activeLink).getPropertyValue(
      "--total-padding-inline",
    )
    const blockPadding = getComputedStyle(activeLink).getPropertyValue(
      "--total-padding-block",
    )
    const outlineWidth =
      getComputedStyle(activeLink).getPropertyValue("--outline-width")

    const width = `calc(${Math.round(targetWidth)}px + ${inlinePadding})`
    const height = `calc(${Math.round(targetHeight)}px + ${blockPadding} + (${outlineWidth} * 2))`

    const parentRect = pill.offsetParent?.getBoundingClientRect()

    const originX = parentRect?.left ?? 0
    const originY = parentRect?.top ?? 0

    dims = {
      width,
      height,
      left: targetLeft - originX + targetWidth / 2,
      top: targetTop - originY + targetHeight / 2,
    }

    return dims
  }

  useEffect(() => {
    if (!pillRef.current || !linkRefs.current.length) return
    const firstLink = linkRefs.current[0]
    if (!firstLink) return
    const pill = pillRef.current
    const { left, top } = getPillDims(firstLink)
    gsap.set(pill, {
      xPercent: -50,
      yPercent: -50,
      left,
      top,
    })
  }, [pillRef])

  useEffect(() => {
    if (!pillRef.current) return
    const pill = pillRef.current

    if (activeLink) {
      const { width, height, left, top } = getPillDims(activeLink)

      gsap.to(pill, {
        opacity: 1,
        width,
        height,
        xPercent: -50,
        yPercent: -50,
        left,
        top,
        duration: 0.2,
        ease: "power2.inOut",
      })
    } else {
      gsap.to(pill, {
        opacity: 0,
        width: 0,
        height: 0,
        yPercent: -50,
        duration: 0.2,
        ease: "power2.inOut",
      })
    }
  }, [activeLink])

  useEffect(() => {
    const currentLinks = linkRefs.current
    if (currentLinks.length === 0) return

    const handleMouseEnter = (event: MouseEvent) => {
      const link = event.target as HTMLLIElement
      setActiveLink(link)
    }

    const handleMouseLeave = (event: MouseEvent) => {
      const link = event.target as HTMLLIElement
      requestAnimationFrame(() => {
        setActiveLink((current) => (current === link ? null : current))
      })
    }

    currentLinks.forEach((link) => {
      link.addEventListener("mouseenter", handleMouseEnter)
      link.addEventListener("mouseleave", handleMouseLeave)
    })

    return () => {
      currentLinks.forEach((link) => {
        link.removeEventListener("mouseenter", handleMouseEnter)
        link.removeEventListener("mouseleave", handleMouseLeave)
      })
    }
  }, [links])

  return (
    <>
      <div
        id="main-nav"
        inert={!isFullSize && !isOpen}
        ref={navRef}
        data-element="main-nav"
        data-open={isOpen}
        className={s.mainNavWrapper}
      >
        <nav aria-label="Main navigation" className={s.mainNav}>
          <ul className={s.navList}>
            <div ref={pillRef} className={s.pill} />
            {links?.map((link, i) => (
              <li
                style={{
                  transitionDelay: isOpen
                    ? `${(i + 1.5) * STAGGER_DELAY}s`
                    : undefined,
                }}
                key={i}
                className={s.navItem}
                ref={(el) => {
                  if (el) linkRefs.current[i] = el
                }}
              >
                <Link
                  href={link.url}
                  aria-current={isNavLinkActive(link.url) ? "page" : undefined}
                  target={link.target ?? undefined}
                  rel={
                    link.target === "_blank" ? "noopener noreferrer" : undefined
                  }
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <button
            type="button"
            aria-label="Open contact us modal"
            onClick={() => {
              if (!isFullSize) setIsOpen(false)
              openContactModal()
            }}
            className={cn(buttonS.primaryButton, s.ctaButton)}
          >
            Work with us
          </button>
        </nav>
      </div>
    </>
  )
}
